import React, { useRef } from 'react';
import { useInvoiceStore } from '../stores/invoiceStore';
import { Calendar } from 'lucide-react';
import { format, parse, isValid, isBefore, startOfDay, addDays } from 'date-fns';
import { useTranslation } from '../hooks/useTranslation';
import { useInputHandling } from '../hooks/useInputHandling';
import { motion, AnimatePresence } from 'framer-motion';
import { useClickOutside } from '../hooks/useClickOutside';
import { toLocalizedFormat, UNIVERSAL_DATE_FORMAT } from '@dutchinvoice/shared-utils';
import { LocalizedDate } from './shared/LocalizedDate';

interface InvoiceDateEditProps {
  isEditing: boolean;
  onEditClick: () => void;
  onSave: () => void;
}

const InvoiceDateEdit: React.FC<InvoiceDateEditProps> = React.memo(({
  isEditing,
  onEditClick,
  onSave,
}) => {
  const { t, locale } = useTranslation();
  const { 
    currentInvoice: { date, dueDate, status, paymentStatus },
    updateDate,
    updatePaymentTerms
  } = useInvoiceStore();

  // Check if invoice is editable based on its status and payment status
  const isInvoiceEditable = status !== 'sent' && paymentStatus !== 'paid';

  const editRef = useRef<HTMLDivElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  
  useClickOutside(editRef, () => {
    if (isEditing) {
      onSave();
    }
  });

  const [selectedDate, setSelectedDate] = React.useState(() => {
    try {
      // Parse from YYYY-MM-DD format
      const parsedDate = parse(date, UNIVERSAL_DATE_FORMAT, new Date());
      return isValid(parsedDate) ? parsedDate : new Date();
    } catch {
      return new Date();
    }
  });
  
  const [paymentDays, setPaymentDays] = React.useState(() => {
    try {
      // Parse from YYYY-MM-DD format
      const invoiceDate = parse(date, UNIVERSAL_DATE_FORMAT, new Date());
      const dueDateObj = parse(dueDate, UNIVERSAL_DATE_FORMAT, new Date());
      if (isValid(invoiceDate) && isValid(dueDateObj)) {
        const diffTime = dueDateObj.getTime() - invoiceDate.getTime();
        return String(Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
      }
      return '14';
    } catch {
      return '14';
    }
  });

  React.useEffect(() => {
    try {
      // Parse from YYYY-MM-DD format
      const parsedDate = parse(date, UNIVERSAL_DATE_FORMAT, new Date());
      if (isValid(parsedDate)) {
        setSelectedDate(parsedDate);
      }
    } catch {
      console.error('Error updating date');
    }
  }, [date]);

  const handleSave = React.useCallback(() => {
    const days = parseFloat(paymentDays.replace(/[^\d.]/g, '')) || 0;
    
    // First update the payment terms
    updatePaymentTerms(`${days} days`);
    
    // Format the date in YYYY-MM-DD format
    const universalDateFormat = format(selectedDate, UNIVERSAL_DATE_FORMAT);
    
    // Update the date - the store will calculate the due date based on the payment terms
    updateDate(universalDateFormat);
    
    onSave();
  }, [paymentDays, selectedDate, updateDate, updatePaymentTerms, onSave]);

  const isPastDate = isBefore(selectedDate, startOfDay(new Date()));

  const { inputProps: dateInputProps, handleKeyDown } = useInputHandling({
    onSave: handleSave,
    isEditing,
    value: format(selectedDate, 'yyyy-MM-dd'),
    onChange: (value) => {
      try {
        const newDate = new Date(value);
        if (isValid(newDate)) {
          setSelectedDate(newDate);
          // Update with YYYY-MM-DD format
          updateDate(format(newDate, UNIVERSAL_DATE_FORMAT));
        }
      } catch {
        console.error('Error handling date change');
      }
    }
  });

  const handlePaymentDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value === '' || parseInt(value) <= 365) {
      setPaymentDays(value);
    }
  };

  return (
    <div className="relative w-full md:w-auto">
      <div 
        ref={displayRef}
        className={`rounded-xl p-4 ${isEditing ? 'invisible' : 'visible'} ${
          isInvoiceEditable ? 'cursor-pointer group' : 'cursor-not-allowed pointer-events-none'
        } transition-all duration-200`}
        onClick={!isEditing && isInvoiceEditable ? onEditClick : undefined}
      >
        <div className="relative flex flex-col items-end">
          {/* Hover indicator */}
          <div className="absolute inset-0 -m-4 rounded-xl bg-gray-50/0 group-hover:bg-gray-50/80 transition-all duration-300" />
          
          {/* Edit indicator */}
          {isInvoiceEditable && (
            <div className="absolute -top-7 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-lg">
                {t('actions.clickToEdit')}
              </span>
            </div>
          )}

          {/* Content */}
          <div className="relative space-y-4 w-full md:w-auto">
            {/* Dates section */}
            <div className="flex justify-between md:items-start gap-8">
              <div className="flex flex-col items-end">
                <span className="text-base font-normal text-gray-900 leading-tight tracking-[-0.01em]">
                  <LocalizedDate date={date} />
                </span>
                <span className="text-[11px] font-medium text-gray-400 leading-tight mt-1">{t('invoice.date')}</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-base font-normal text-gray-900 leading-tight tracking-[-0.01em]">
                  <LocalizedDate date={dueDate} />
                </span>
                <span className="text-[11px] font-medium text-gray-400 leading-tight mt-1">{t('invoice.dueDate')}</span>
              </div>
            </div>

            {/* Payment terms */}
            <div className="flex items-center justify-end gap-2">
              <span className="text-[11px] font-medium text-gray-400 leading-none">{t('invoice.paymentTerms')}</span>
              <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                <span className="text-[14px] font-normal leading-none text-gray-900">{paymentDays}</span>
                <span className="text-[11px] font-medium leading-none text-gray-500">{t('invoice.days')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isEditing && (
          <motion.div
            ref={editRef}
            key="edit"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-0 right-0 z-50"
            onKeyDown={handleKeyDown}
          >
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-[0_0_20px_rgba(0,0,0,0.08)] p-4">
              <div className="space-y-4 w-[260px]">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                    {t('invoice.date')}
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      {...dateInputProps}
                      className={`block w-full pl-10 pr-3 py-2 text-[13px] border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 ${
                        isPastDate ? 'text-amber-600' : 'text-gray-900'
                      }`}
                      style={{
                        colorScheme: 'light'
                      }}
                    />
                    <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
                      isPastDate ? 'text-amber-500' : 'text-gray-400'
                    }`} />
                  </div>
                  {isPastDate && (
                    <p className="mt-1.5 text-[11px] font-medium text-amber-600">
                      {t('placeholders.pastDateWarning')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                    {t('invoice.paymentDays')}
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative w-16">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={paymentDays}
                        onChange={handlePaymentDaysChange}
                        className="block w-full px-2 py-2 text-[13px] border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-center"
                        placeholder="14"
                      />
                    </div>
                    <span className="text-[13px] text-gray-600">
                      {t('invoice.days')}
                    </span>
                  </div>
                  <p className="mt-2 text-[11px] font-medium text-gray-500">
                    {t('placeholders.dueDateWillBe', { 
                      date: toLocalizedFormat(format(addDays(selectedDate, parseFloat(paymentDays.replace(/[^\d.]/g, '')) || 0), UNIVERSAL_DATE_FORMAT), locale)
                    })}
                  </p>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {t('actions.save')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export default InvoiceDateEdit; 